import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form } from 'formik'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import AutocompleteField from '@/components/formik/fields/AutocompleteField'
import DatePickerField from '@/components/formik/fields/DatePickerField'
import { AutocompleteOption } from '@/components/ui/inputs/AutocompleteInput'


export type ConsultationsSearchFormValues = {
  patient: string
  type: string
  startDate: {
    $gte: Date | null
    $lte: Date | null
  }
}

export type ConsultationsSearchFormProps = {
  direction?: 'column' | 'row'
  initialValues: ConsultationsSearchFormValues
  onReset?: (values: ConsultationsSearchFormValues) => void
  onSearchPatient?: (value: string) => void
  onSubmit: (values: ConsultationsSearchFormValues) => void
  patientOptions: AutocompleteOption[]
}

export default function ConsultationsSearchForm(props: ConsultationsSearchFormProps) {
  const { direction = 'column', initialValues, onReset, onSearchPatient, onSubmit, patientOptions } = props
  const { t } = useTranslation()


  const handleSearchPatient = useCallback(
    (inputValue: string) => {
      onSearchPatient?.(inputValue)
    },
    [onSearchPatient]
  )

  return (
    <Box sx={{ padding: '0 15px' }}>
      <Typography variant="h2" sx={{ mt: '30px', mb: '30px' }}>
        {t('page.consultations.consultationsSearchForm.title')}
      </Typography>
      <Formik initialValues={initialValues} onSubmit={onSubmit}>
        {(formikProps) => (
          <Form onSubmit={formikProps.handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={direction === 'column' ? 12 : 4}>
                <AutocompleteField
                  label={t('page.consultations.consultationsSearchForm.field.searchByPatient.label')}
                  name="patient"
                  onInputChange={handleSearchPatient}
                  options={patientOptions}
                />
              </Grid>
              <Grid item xs={12} sm={direction === 'column' ? 12 : 4}>
                <DatePickerField fullWidth label={t('page.consultations.consultationsSearchForm.field.startDateFrom.label')} name="startDate.$gte" />
              </Grid>
              <Grid item xs={12} sm={direction === 'column' ? 12 : 4}>
                <DatePickerField fullWidth label={t('page.consultations.consultationsSearchForm.field.startDateTo.label')} name="startDate.$lte" />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="end" spacing={1}>
                  <Button
                    onClick={() => {
                      formikProps.handleReset()
                      onReset?.(formikProps.initialValues)
                    }}
                    variant="neutral"
                  >
                    {t('common:button.reset.label')}
                  </Button>
                  <LoadingButton id="searchButton" type="submit" variant="contained">
                    {t('common:button.submit.label')}
                  </LoadingButton>
                </Stack>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
